import { useEffect, useState } from "react";

const API_URL = "/api";

export default function EditInventoryModal({
  item,
  categories,
  departments,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    name: "",
    category: "",
    department_id: "",
    type: "raw",
    stock: "",
    unit: "Pieces",
    minStock: "",
    notes: "",
  });

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const categoryNames = categories.map((category) =>
    typeof category === "string" ? category : category.name,
  );

  useEffect(() => {
    if (!item) return;

    setForm({
      name: item.name || "",
      category: item.category || "",
      department_id: item.department_id || "",
      type: item.type || "raw",
      stock: item.stock ?? 0,
      unit:
        item.unit && ["Kg", "Litres", "Pieces"].includes(item.unit)
          ? item.unit
          : "Pieces",
      minStock: item.minStock ?? 0,
      notes: "",
    });

    fetchHistory();
  }, [item]);

  const fetchHistory = async () => {
    if (!item?.id) return;

    setLoadingHistory(true);

    try {
      const response = await fetch(
        `${API_URL}/inventory/items/${item.id}/history`,
        {
          credentials: "include",
        },
      );

      const data = await response.json();

      if (data.status === "success") {
        setHistory(data.data || []);
      }
    } catch (err) {
      console.error("Could not load inventory edit history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const saveChanges = async () => {
    setError("");

    if (!form.name.trim()) {
      setError("Item name is required.");
      return;
    }

    if (!form.category) {
      setError("Category is required.");
      return;
    }

    if (!form.department_id) {
      setError("Department is required.");
      return;
    }

    if (Number(form.stock) < 0) {
      setError("Stock cannot be negative.");
      return;
    }

    if (Number(form.minStock) < 0) {
      setError("Minimum stock cannot be negative.");
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(
        `${API_URL}/inventory/items/${item.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            name: form.name.trim(),
            category: form.category,
            department_id: Number(form.department_id),
            type: form.type,
            stock: Number(form.stock),
            unit: form.unit,
            minStock: Number(form.minStock),
            notes: form.notes.trim() || "Inventory item updated",
          }),
        },
      );

      const data = await response.json();

      if (data.status !== "success") {
        throw new Error(data.message || "Could not update inventory item");
      }

      await onSaved();
      onClose();
    } catch (err) {
      setError(err.message || "Could not save inventory changes.");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";

    return new Date(value).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-2xl border border-white/10 bg-[#0b0b0f] p-6 shadow-2xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-serif">Edit Inventory Item</h2>
            <p className="mt-1 text-sm text-gray-400">
              Stock changes are saved as an adjustment in the inventory ledger.
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-xl text-gray-400 transition hover:text-white"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="mb-5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-gray-400">Item Name</label>
            <input
              value={form.name}
              onChange={(e) =>
                setForm((previous) => ({
                  ...previous,
                  name: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2"
              placeholder="Item name"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-400">
              Department
            </label>
            <select
              value={form.department_id}
              onChange={(e) =>
                setForm((previous) => ({
                  ...previous,
                  department_id: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            >
              <option value="">Select Department</option>

              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-400">Category</label>
            <select
              value={form.category}
              onChange={(e) =>
                setForm((previous) => ({
                  ...previous,
                  category: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            >
              <option value="">Select Category</option>

              {categoryNames.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-400">Item Type</label>
            <select
              value={form.type}
              onChange={(e) =>
                setForm((previous) => ({
                  ...previous,
                  type: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            >
              <option value="raw">Raw Material</option>
              <option value="finished">Finished Goods</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-400">
              Current Stock
            </label>
            <input
              type="number"
              min="0"
              step="0.001"
              value={form.stock}
              onChange={(e) =>
                setForm((previous) => ({
                  ...previous,
                  stock: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-amber-400/30 bg-black/40 px-4 py-2"
              placeholder="Current stock"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-400">Unit</label>
            <select
              value={form.unit}
              onChange={(e) =>
                setForm((previous) => ({
                  ...previous,
                  unit: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            >
              <option value="Kg">Kg</option>
              <option value="Litres">Litres</option>
              <option value="Pieces">Pieces</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-400">
              Minimum Stock
            </label>
            <input
              type="number"
              min="0"
              step="0.001"
              value={form.minStock}
              onChange={(e) =>
                setForm((previous) => ({
                  ...previous,
                  minStock: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2"
              placeholder="Minimum stock"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-400">Branch</label>
            <div className="rounded-lg border border-white/10 bg-black/20 px-4 py-2 text-gray-300">
              {item.branch || "-"}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="mb-1 block text-sm text-gray-400">
              Edit Reason
            </label>
            <textarea
              value={form.notes}
              onChange={(e) =>
                setForm((previous) => ({
                  ...previous,
                  notes: e.target.value,
                }))
              }
              rows="3"
              className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2"
              placeholder="Example: Physical stock count correction"
            />
          </div>
        </div>

        <div className="mt-8 border-t border-white/10 pt-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-medium">Edit History</h3>
              <p className="mt-1 text-xs text-gray-500">
                Every inventory adjustment records the editor and employee code.
              </p>
            </div>

            <button
              onClick={fetchHistory}
              className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-gray-300 hover:bg-white/5"
            >
              Refresh
            </button>
          </div>

          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-gray-400">
                <tr>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Edited By</th>
                  <th className="px-4 py-3 text-left">Employee Code</th>
                  <th className="px-4 py-3 text-center">Adjustment</th>
                  <th className="px-4 py-3 text-left">Reason</th>
                </tr>
              </thead>

              <tbody>
                {loadingHistory ? (
                  <tr>
                    <td colSpan="5" className="p-5 text-center text-gray-500">
                      Loading edit history…
                    </td>
                  </tr>
                ) : history.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="p-5 text-center text-gray-500">
                      No inventory edits recorded yet.
                    </td>
                  </tr>
                ) : (
                  history.map((entry) => (
                    <tr
                      key={entry.id}
                      className="border-t border-white/10"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatDate(entry.created_at)}
                      </td>

                      <td className="px-4 py-3">
                        {entry.edited_by_name || "System"}
                      </td>

                      <td className="px-4 py-3">
                        {entry.edited_by_employee_id || "-"}
                      </td>

                      <td
                        className={`px-4 py-3 text-center font-medium ${
                          Number(entry.quantity) < 0
                            ? "text-red-400"
                            : Number(entry.quantity) > 0
                              ? "text-emerald-400"
                              : "text-amber-300"
                        }`}
                      >
                        {Number(entry.quantity) > 0 ? "+" : ""}
                        {entry.quantity}
                      </td>

                      <td className="max-w-xs px-4 py-3 text-xs text-gray-400">
                        {entry.notes || entry.reference || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-xl border border-white/20 px-5 py-2 text-sm hover:bg-white/5 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={saveChanges}
            disabled={saving}
            className="rounded-xl bg-amber-400 px-5 py-2 text-sm font-medium text-black hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}